/**
 * "Update booking for" — which member of the account an appointment's record is
 * shown and acted on for in phr.
 *
 * A MAPPING stored on the appointment itself, in its own field
 * `updatedBookingFor: { memberPatientId, updatedBy, updatedByType, updatedAt }`
 * (api-appointment-service, written by PUT /v1/appointment/:id/booking-for).
 * patientID and bookedFor are never changed, and nothing outside phr reads the
 * new field. Because it is part of the appointment document, it arrives with
 * every appointment read — no extra request.
 *
 * Every phr screen that resolves "whose record is this appointment" asks here
 * first and keeps its own bookedFor / patientID resolution as the fallback.
 */

/** The member the appointment itself is for: bookedFor, or the holder when that is 0. */
export const bookedMemberId = (a) =>
  a ? (a.bookedFor && a.bookedFor !== 0 ? a.bookedFor : a.patientID) : undefined;

/**
 * The mapped member, or null when the appointment is not switched (no field, or
 * a field pointing back at the member it is booked for). Callers use it FIRST:
 *   overrideMemberId(a) || (a.bookedFor && a.bookedFor !== 0 ? a.bookedFor : a.patientID)
 */
export const overrideMemberId = (a) => {
  const m = a && a.updatedBookingFor && Number(a.updatedBookingFor.memberPatientId);
  if (!(m > 0)) return null;
  return m === Number(bookedMemberId(a)) ? null : m;
};

/** Who the appointment is booked for, while it is switched to someone else; else null. */
export const originalMemberId = (a) => (overrideMemberId(a) ? Number(bookedMemberId(a)) : null);

/* A route the service does not have yet answers with Echo's own 404 body,
   { message: "Not Found" }; the booking-for handler wraps every error of its own
   in an object ({ code, message, errorMessage }). So a bare "Not Found" string
   means the backend is not deployed yet. */
const isRouteMissing = (err) => !!err && err.message === "Not Found";

/** A readable reason from either error shape phr's API layer hands back. */
export const bookingForErrorText = (err) => {
  if (isRouteMissing(err)) return "It isn’t available on the server yet.";
  const m = err && err.message;
  if (typeof m === "string") return m;
  if (m && typeof m.message === "string") return m.message;
  return "";
};

/**
 * The Rx scratch is parked per appointment; while switched it is parked per
 * MEMBER too, or a draft started for one person is restored onto the other's
 * prescription (its fullName, instructions, referrals, follow-up and
 * investigations all carry over). Unswitched appointments keep the plain key,
 * so drafts parked before this change still restore.
 */
export const scratchKeyFor = (appointmentId, memberId) =>
  memberId ? `${appointmentId}.m${memberId}` : appointmentId;
